/**
 * P3-23: CraftButton —— Workshop craft 列表按钮。
 *
 * 迁移自 `com.nuclearunicorn.game.ui.CraftButton` + CraftButtonController
 *（js/workshop.js:2881-3097）。
 *
 * 行为对齐：
 * - 名称：controller.getName 产出 HTML（含 craft.value 与 mechanization 后进度）
 * - 描述：aria-description 取 model.description
 * - 可见：`craft.unlocked`
 * - 禁用：资源不足
 * - domNode 类：`craftOn`（craft.value > 0）+ `bldEnabled`/`bldlackResConvert`
 * - 链接：mechanization 后显示 `[-]/[-25]/[-100]/[+]/[+25]/[+100]` 下拉链接组
 * - 点击：`controller.buyItem(modelRef.current, event)` → craft 1 个
 * - tooltip：`ButtonModernHelper.getTooltipHTML` 直取
 */
import { useCallback, type MouseEvent } from "react";
import { GameButton } from "../../components/GameButton";
import { useCraftButtonModel, type CraftLinkInfo } from "./useWorkshopModel";

interface LinkItem extends CraftLinkInfo {
  handler: () => void;
}

interface CraftLinkListProps {
  /** 来自快照的链接元信息（标题/id），不携带 handler */
  links: CraftLinkInfo[];
  /** 事件触发时从 modelRef 读取当前 handler，避免渲染期访问 ref */
  getItems: () => LinkItem[] | undefined;
}

/**
 * Craft 工程师分配链接下拉：与 BuildingBtn 的 LinkListDropdown 同构，
 * 但 handler 通过 getItems 延迟到事件回调中读取。
 */
function CraftLinkList({ links, getItems }: CraftLinkListProps) {
  if (!links.length) {
    return null;
  }
  const [root, ...rest] = links;

  const handleClick = (index: number) => (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const items = getItems();
    const item = items?.[index];
    item?.handler?.();
  };

  return (
    <div style={{ float: "right" }}>
      <a
        href="#"
        className={root.id ? root.id + "Link" : undefined}
        style={{ display: "block", float: "right" }}
        title={root.title}
        onClick={handleClick(0)}
        dangerouslySetInnerHTML={{ __html: root.title }}
      />
      {rest.length > 0 && (
        <div
          className="linkContent"
          style={{
            display: "none",
            position: "absolute",
            float: "right",
            marginTop: "35px",
            zIndex: 100,
          }}
        >
          {rest.map((item, i) => (
            <a
              key={item.id ?? item.title}
              href="#"
              className="dropdown-link"
              style={{ display: "block" }}
              title={item.title}
              onClick={handleClick(i + 1)}
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CraftButtonProps {
  craftName: string;
}

export function CraftButton({ craftName }: CraftButtonProps) {
  const { snap, modelRef, controller, getTooltip } = useCraftButtonModel(craftName);

  const onClick = useCallback(
    (event: MouseEvent) => {
      controller?.buyItem?.(modelRef.current, event);
    },
    [controller, modelRef],
  );

  const getUnassign = useCallback(
    () => (modelRef.current?.unassignCraftLinks as LinkItem[] | undefined),
    [modelRef],
  );
  const getAssign = useCallback(
    () => (modelRef.current?.assignCraftLinks as LinkItem[] | undefined),
    [modelRef],
  );

  if (!snap || !snap.visible) {
    return null;
  }

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      domClassName={snap.domClassName}
      getTooltip={getTooltip}
      onClick={onClick}
      getSelectedObject={() => modelRef.current}
    >
      {snap.unassignLinks.length > 0 && (
        <CraftLinkList links={snap.unassignLinks} getItems={getUnassign} />
      )}
      {snap.assignLinks.length > 0 && (
        <CraftLinkList links={snap.assignLinks} getItems={getAssign} />
      )}
    </GameButton>
  );
}
